// Preserve-and-restore a deep-link destination across the login flow.
//
// When a logged-out user hits a protected route, PrivateRoute stashes the
// path they were trying to reach here; each login-completion site (the login
// form and the SSO callbacks) then consumes it and sends the user there
// instead of their role-default dashboard.
//
// Carrier is sessionStorage on purpose: it survives the OAuth round-trip to
// external providers (same tab/origin on return) and page reloads, and it
// survives the force-logout Login.js runs on mount -- authService.logout()
// only removes its own specific keys and never calls sessionStorage.clear(),
// so this key is left intact. (react-router location.state would be lost the
// moment the browser leaves for the OAuth provider.)

const SESSION_KEY = "postLoginRedirect";

// Entry / auth paths that should never be treated as a post-login destination
// (redirecting back to them would loop or be pointless).
const EXCLUDED_PREFIXES = [
  "/login",
  "/clever", // /clever-login, /clever-sso-login, /clever-securesync-login, /clever-callback
  "/google-login",
  "/classlink-login",
  "/register",
];

// Only same-origin, app-relative paths are allowed. Rejects absolute URLs
// (http://, https://) and protocol-relative (//host) to avoid open redirects.
const isSafeInternalPath = (path) => {
  if (typeof path !== "string" || !path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path === "/") return false;
  return !EXCLUDED_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`)
  );
};

// `location` is a react-router / window location. Store pathname+search+hash.
export const savePostLoginRedirect = (location) => {
  if (!location) return;
  const path = `${location.pathname || ""}${location.search || ""}${
    location.hash || ""
  }`;
  if (!isSafeInternalPath(path)) return;
  try {
    sessionStorage.setItem(SESSION_KEY, path);
  } catch (e) {
    // sessionStorage may be unavailable (private mode etc.) -- fail soft.
  }
};

// Read and clear (one-shot). Returns a safe internal path or null.
export const consumePostLoginRedirect = () => {
  let path = null;
  try {
    path = sessionStorage.getItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  } catch (e) {
    return null;
  }
  return isSafeInternalPath(path) ? path : null;
};

// Call at a login-completion site BEFORE the role-based redirect. If a stored
// destination exists, navigate there and report that the redirect was handled.
export const maybeRedirectToNext = (history) => {
  const next = consumePostLoginRedirect();
  if (next) {
    history.push(next);
    return true;
  }
  return false;
};
